import "./Cotizaciontabla.scss"
import React, {useState, useEffect, useRef} from 'react';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { useNavigate } from "react-router-dom";
import { Calendar } from "primereact/calendar";
import { UpdateValue } from "../../../utilidades/Validaciones";
import { Toolbar } from "primereact/toolbar";
import { getCotList } from "./Integracion";
import { Checkbox } from "primereact/checkbox";
import { OverlayPanel } from "primereact/overlaypanel";

// Constante para la clave de localStorage
const STORAGE_KEY = 'cotizacionesFilters';

/**
 * Guarda los filtros en localStorage
 * @param {Object} dateFilters - Filtros de fecha
 * @param {Array} selectedEstatus - Estatus seleccionados
 */
const saveFiltersToStorage = (dateFilters, selectedEstatus) => {
    try {
        const filtersToSave = {
            start: dateFilters.start.value.toISOString(),
            end: dateFilters.end.value.toISOString(),
            estatus: selectedEstatus.map(item => item.key)
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (error) {
        console.error('Error al guardar filtros en localStorage:', error);
    }
};

/**
 * Carga los filtros desde localStorage
 * @param {Array} categories - Categorías disponibles
 * @returns {Object|null} - Filtros cargados o null si no existen
 */
const loadFiltersFromStorage = (categories) => {
    try {
        const savedFilters = localStorage.getItem(STORAGE_KEY);
        if (savedFilters) {
            const parsed = JSON.parse(savedFilters);
            return {
                dateFilters: {
                    start: {value: new Date(parsed.start)},
                    end: {value: new Date(parsed.end)}
                },
                selectedEstatus: parsed.estatus
                    ? categories.filter(cat => parsed.estatus.includes(cat.key))
                    : []
            };
        }
    } catch (error) {
        console.error('Error al cargar filtros desde localStorage:', error);
    }
    return null;
};

/**
 * Limpia los filtros guardados en localStorage
 */
const clearFiltersFromStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Error al limpiar filtros de localStorage:', error);
    }
};

const CotizacionTabla = () => {
    const navigate = useNavigate();
    const overlayRef = useRef(null);

    const categories = [
        { name: 'Por Cotizar', key: 10 },
        { name: 'Por confirmar', key: 20 },
        { name: 'Por Concluir', key: 40 },
        { name: 'Concluidas', key: 60 },
        { name: 'Canceladas', key: 90 }
    ];

    // Cargar filtros desde localStorage o usar valores por defecto
    const savedFilters = loadFiltersFromStorage(categories);

    const [services, setServices] = useState(null);
    const [dateFilters, setDateFilters] = useState(
        savedFilters?.dateFilters || {
            start: {value: new Date()},
            end: {value: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)},
        }
    );
    const [selectedEstatus, setSelectedEstatus] = useState(savedFilters?.selectedEstatus || []);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [loading, setLoading] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [selectedCostos, setSelectedCostos] = useState(null);

    const getSeverity = (status) => {
        switch (status) {
            case 'VENDIDO':
                return 'success';
            case 'COTIZADO':
                return 'info';
            case 'POR CONFIRMAR':
                return 'info';
            case 'CANCELADO':
                return 'danger';
            case 'VIAJE CANCELADO':
                return 'danger';
            default:
                return 'primary';
        }
    };

    /**
     * Resetea los filtros a valores por defecto
     */
    const resetFilters = () => {
        const defaultDateFilters = {
            start: {value: new Date()},
            end: {value: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)},
        };
        setDateFilters(defaultDateFilters);
        setSelectedEstatus([]);
        clearFiltersFromStorage();
    };

    /**
     * Maneja el cambio en los checkboxes de estatus
     */
    const onEstatusChange = (e) => {
        const selectedValue = e.value;
        let updatedSelection = [...selectedEstatus];

        if (e.checked) {
            updatedSelection.push(selectedValue);
        } else {
            updatedSelection = updatedSelection.filter(
                item => item.key !== selectedValue.key
            );
        }

        setSelectedEstatus(updatedSelection);
    };

    /**
     * Cargar datos de cotizaciones
     */
    const loadCotizaciones = async () => {
        try {
            setLoading(true);
            const data = await getCotList(dateFilters, selectedEstatus);

            const parsedData = data.map(cotizacion => {
                try {
                    return {
                        ...cotizacion,
                        COSTOS: cotizacion.COSTOS ? JSON.parse(cotizacion.COSTOS) : []
                    };
                } catch (error) {
                    console.error(`Error al parsear COSTOS para cotización ${cotizacion.ID}:`, error);
                    return {
                        ...cotizacion,
                        COSTOS: []
                    };
                }
            });

            setServices(parsedData);
        } catch (error) {
            console.error('Error al cargar cotizaciones:', error);
        } finally {
            setLoading(false);
        }
    };


    // Guardar filtros en localStorage cada vez que cambien
    useEffect(() => {
        if (dateFilters.start.value && dateFilters.end.value) {
            saveFiltersToStorage(dateFilters, selectedEstatus);
        }
    }, [dateFilters, selectedEstatus]);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadCotizaciones().then(r => {});
    }, []);

    const formatCurrency = (value) => {
        return value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    /**
     * ✅ Template para mostrar los costos totales con OverlayPanel
     */
    const costosBodyTemplate = (rowData) => {
        // Si no hay costos, retornar vacío
        if (!rowData.COSTOS || rowData.COSTOS.length === 0) {
            return <span style={{ color: '#6c757d' }}>-</span>;
        }

        // Calcular el total sumando todos los costos
        const totalCostos = rowData.COSTOS.reduce((acc, item) => acc + (item.COSTOS || 0), 0);

        // solo reservados
        /*const totalCostos = rowData.COSTOS
            .filter(item => item.RESERVADO === true || item.RESERVADO === 1)
            .reduce((acc, item) => acc + (item.COSTOS ?? 0), 0);*/


        // Si solo hay un costo, mostrar sin icono
        if (rowData.COSTOS.length === 1) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{formatCurrency(totalCostos)}</span>
                </div>
            );
        }

        // Si hay múltiples costos, mostrar con icono clickeable
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer'
                }}
                onClick={(e) => {
                    setSelectedCostos(rowData.COSTOS);
                    overlayRef.current.toggle(e);
                }}
            >
                <span>{formatCurrency(totalCostos)}</span>
                <i className="pi pi-info-circle" style={{ color: '#2196F3', fontSize: '0.9rem' }}></i>
            </div>
        );
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const renderHeader = () => {
        return (
            <div className="table-service-header">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Buscar" />
                </IconField>
                <Button icon="pi pi-plus" label="Nuevo" onClick={() => navigate(`/suma-travel/cotizaciones/nueva`)}></Button>
            </div>
        );
    };

    const handleNavigate = (id) => {
        navigate(`/suma-travel/cotizaciones/${id}`);
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.ESTATUS} severity={getSeverity(rowData.ESTATUS)} />;
    };

    const balanceBodyTemplate = (rowData) => {
        if (rowData.PRECIO === null) return null;
        return formatCurrency(rowData.PRECIO);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => handleNavigate(rowData.ID)} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", gap: '10px', alignItems: 'center'}}>
                    <div className="columna">
                        <Calendar value={dateFilters.start.value}
                                  onChange={(e) => {
                                      UpdateValue(e, "start", dateFilters, setDateFilters)
                                  }} showIcon dateFormat="dd/mm/yy" readOnlyInput={true}/>
                    </div>
                    <div className="columna">
                        <Calendar value={dateFilters.end.value}
                                  onChange={(e) => {
                                      UpdateValue(e, "end", dateFilters, setDateFilters)
                                  }} showIcon dateFormat="dd/mm/yy" readOnlyInput={true}/>
                    </div>
                </div>
            </>
        )
    }

    const centerToolbarTemplate = () => {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", gap: '5px', alignItems: 'center'}}>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined p-button-secondary"
                        onClick={resetFilters}
                        tooltip="Resetear filtros"
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            </>
        )
    }

    const rightToolbarTemplate = () => {
        return (
            <div style={{display: "flex", flexDirection: "row", gap: '10px'}}>
                <div style={{display: "flex", flexDirection: "row", gap: '10px'}}>
                    {categories.map((category) => {
                        return (
                            <div key={category.key}>
                                <Checkbox inputId={category.key} name="category" value={category}
                                          onChange={onEstatusChange}
                                          checked={selectedEstatus.some((item) => item.key === category.key)}
                                          style={{marginRight: '8px'}}
                                />
                                <label htmlFor={category.key} className="ml-2">
                                    {category.name}
                                </label>
                            </div>
                        );
                    })}
                </div>
                <Button label="Filtrar" icon="pi pi-filter" style={{marginLeft: '2rem'}} onClick={loadCotizaciones} disabled={loading}/>
            </div>
        )
    }

    const header = renderHeader();

    return (
        <div className="cotizaciones-table-wrapper">
            {/* ✅ OverlayPanel para mostrar desglose de costos */}
            <OverlayPanel ref={overlayRef} style={{ width: '400px' }}>
                {selectedCostos && (
                    <div style={{ padding: '12px' }}>
                        <h4 style={{
                            margin: '0 0 12px 0',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}>
                            Desglose de Costos
                        </h4>

                        {selectedCostos.map((costo, index) => (
                            <div
                                key={costo.ID}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 0',
                                    borderBottom: index < selectedCostos.length - 1 ? '1px solid #f0f0f0' : 'none'
                                }}
                            >
                                <span style={{ color: '#495057' }}>Cotización #{costo.ID}</span>
                                <span style={{ color: '#495057' }}>R: {costo.RESERVADO ? '✔️' : '❌'}</span>
                                <span style={{ fontWeight: '600', color: '#212529' }}>
                                    {formatCurrency(costo.COSTOS)}
                                </span>
                            </div>
                        ))}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '2px solid #dee2e6'
                        }}>
                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>Total</span>
                            <span style={{ fontWeight: 'bold', fontSize: '15px', color: '#2196F3' }}>
                                {formatCurrency(selectedCostos.reduce((acc, item) => acc + (item.COSTOS || 0), 0))}
                            </span>
                        </div>
                    </div>
                )}
            </OverlayPanel>
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate} center={centerToolbarTemplate} style={{marginTop:'2rem', marginBottom:'2rem'}}/>
                <DataTable
                    key='service-datatable'
                    value={services}
                    showGridlines
                    loading={loading}
                    dataKey="ID"
                    scrollable
                    scrollHeight="calc(100vh - 260px)"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} cotizaciones"
                    filters={filters}
                    globalFilterFields={['ID', 'ESTATUS', 'FECHA_INICIAL', 'FECHA_FINAL', 'LUGAR_DESTINO', 'UNIDAD', 'CONTACTO_NOMBRE', 'FUENTE']}
                    header={header}
                    emptyMessage="No se encontraron cotizaciones">
                    <Column body={actionBodyTemplate} exportable={false} style={{minWidth: '5rem'}}></Column>
                    <Column header="ID" field="ID"  style={{minWidth: '5rem'}}/>
                    <Column header="ESTATUS" field="ESTATUS"  style={{minWidth: '12rem'}} body={statusBodyTemplate}/>
                    <Column header="FECHA CREACION" field="FECHA" style={{minWidth: '10rem'}}/>
                    <Column header="FECHA Y HORA SALIDA" field="FECHA_INICIAL" style={{minWidth: '10rem'}}/>
                    <Column header="FECHA Y HORA FINAL" field="FECHA_FINAL" style={{minWidth: '10rem'}}/>
                    <Column header="DESTINO" field="LUGAR_DESTINO" style={{minWidth: '12rem'}}/>
                    <Column header="PASAJEROS" field="PASAJEROS" style={{minWidth: '6rem'}}/>
                    <Column header="UNIDAD" field='UNIDAD' style={{minWidth: '8rem'}}/>
                    <Column header="PRECIO" field='PRECIO' style={{minWidth: '8rem'}} body={balanceBodyTemplate}/>
                    <Column header="COSTOS" style={{minWidth: '10rem'}} body={costosBodyTemplate}/>
                    <Column header="CONTACTO" field='CONTACTO_NOMBRE' style={{minWidth: '14rem'}}/>
                    <Column header="COTIZADO" field='COTIZADO' style={{minWidth: '10rem'}}/>
                    <Column header="FUENTE" field='FUENTE' style={{minWidth: '12rem'}}/>
                </DataTable>
            </div>
        </div>
    );
}

export default CotizacionTabla
